import React, { useEffect, useId, useRef } from "react";
import { Button, cn } from "@fmc-digital/ui";

export const INFO_MODAL_NEW_TRANSFER_URL = "https://www.freedommortgage.com/newtransfer";

export type InfoModalAction = {
  label: string;
  onClick?: () => void;
  variant?: "filled" | "outlined";
};

export type InfoModalVariant = "default" | "loginSessionExpiry";

export interface InfoModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  body?: React.ReactNode;
  primaryAction?: InfoModalAction;
  secondaryAction?: InfoModalAction;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  hideSecondaryAction?: boolean;
  variant?: InfoModalVariant;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  // Deprecated: kept for backward compatibility during migration.
  onContactSupport?: () => void;
}

const getFocusableElements = (container: HTMLElement) =>
  Array.from(
    container.querySelectorAll<HTMLElement>(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
    )
  ).filter(el => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true");

export const InfoModal: React.FC<InfoModalProps> = ({
  open,
  onClose,
  title,
  body,
  primaryAction,
  secondaryAction,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  hideSecondaryAction = false,
  variant = "default",
  ariaLabelledBy,
  ariaDescribedBy,
  onContactSupport,
}) => {
  const modalRef = useRef<HTMLDialogElement>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  const generatedTitleId = useId();
  const generatedDescriptionId = useId();
  const isLoginSessionExpiry = variant === "loginSessionExpiry";

  const resolvedTitleId = ariaLabelledBy ?? generatedTitleId;
  const resolvedDescriptionId = ariaDescribedBy ?? generatedDescriptionId;

  const resolvedTitle = title ?? "Freedom Loan Number";

  const resolvedBody =
    body ?? (
      <>
        If your loan was just transferred to Freedom Mortgage from another servicer, please note that your previous loan number will not work in our system. Your new Freedom loan number can be found on the Goodbye Letter from your prior servicer, Freedom Mortgage Welcome Email, or Welcome Letter. Visit the{" "}
        <a
          href={INFO_MODAL_NEW_TRANSFER_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#00273A",
            fontWeight: 500,
            textDecorationLine: "underline",
            textDecorationStyle: "solid",
            textDecorationSkipInk: "none",
            textDecorationThickness: "auto",
            textUnderlineOffset: "auto",
            textUnderlinePosition: "from-font",
            fontFamily: "Poppins, var(--Font-Family-body, sans-serif)",
            fontSize: "15px",
            lineHeight: "normal",
          }}
        >
          New Transfer
        </a>
        {" "}page for more information about your servicing transfer.
      </>
    );

  const resolvedSecondaryAction: InfoModalAction =
    secondaryAction ?? {
      label: "Visit New Transfer",
      variant: "outlined",
      onClick:
        onContactSupport ??
        (() => {
          window.open(INFO_MODAL_NEW_TRANSFER_URL, "_blank", "noopener,noreferrer");
        }),
    };

  const resolvedPrimaryAction: InfoModalAction =
    primaryAction ?? {
      label: "Ok",
      variant: "filled",
      onClick: onClose,
    };

  useEffect(() => {
    if (!open) {
      return;
    }

    lastFocusedElementRef.current = document.activeElement as HTMLElement;
    modalRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!modalRef.current) {
        return;
      }

      if (event.key === "Escape" && closeOnEscape) {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = getFocusableElements(modalRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        modalRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      lastFocusedElementRef.current?.focus();
    };
  }, [closeOnEscape, onClose, open]);

  useEffect(() => {
    const dialog = modalRef.current;
    if (!open || !closeOnOverlayClick || !dialog) {
      return;
    }

    // On mobile the panel fills the dialog, so there is no dismissible backdrop.
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target === dialog) {
        onClose();
      }
    };

    dialog.addEventListener("pointerdown", handlePointerDown);
    return () => {
      dialog.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [closeOnOverlayClick, onClose, open]);

  if (!open) return null;

  const actionButtonClassName = cn(
    "font-medium flex justify-center items-center gap-1 px-6 py-2 rounded-[100px] font-poppins text-[15px] normal-case",
    isLoginSessionExpiry ? "w-auto" : "w-full md:w-auto",
  );

  let actionsClassName: string;
  if (isLoginSessionExpiry) {
    actionsClassName = "items-center justify-end bg-[#FEFEFE] px-6 py-4";
  } else if (hideSecondaryAction) {
    actionsClassName = cn(
      "mt-auto w-full md:mt-0",
      "px-4 py-4 md:px-8 md:py-4",
      "flex-col md:flex-row md:justify-center",
    );
  } else {
    actionsClassName = cn(
      "mt-auto w-full md:mt-0",
      "px-4 py-4 md:px-8 md:py-4",
      "flex-col-reverse md:flex-row md:items-center md:justify-between",
    );
  }

  return (
    <dialog
      ref={modalRef}
      open
      aria-modal="true"
      aria-labelledby={resolvedTitleId}
      aria-describedby={resolvedDescriptionId}
      tabIndex={-1}
      data-testid="info-modal"
      className={cn(
        "fixed inset-0 z-50 m-0 flex h-dvh w-screen border-0 p-0",
        isLoginSessionExpiry
          ? "items-center justify-center bg-black/50 px-4 md:px-0"
          : "items-stretch bg-white md:items-center md:justify-center md:bg-black/50",
      )}
    >
      <div
        data-testid="info-modal-panel"
        className={cn(
          "relative flex w-full flex-col px-0 pt-0 pb-0",
          isLoginSessionExpiry
            ? "h-auto min-h-0 w-full items-start overflow-hidden rounded-[32px] bg-[#FEFEFE] shadow-[0px_8px_32px_0px_rgba(16,24,40,0.18)] md:w-[432px] md:max-w-[432px] md:shrink-0 lg:w-[600px] lg:max-w-[600px]"
            : cn(
                "h-full min-h-dvh max-w-none bg-white rounded-none shadow-none",
                "md:h-auto md:min-h-[368px] md:max-w-[600px] md:rounded-[32px]",
                "md:shadow-[0px_8px_32px_0px_rgba(16,24,40,0.18)]",
              ),
        )}
      >
      {showCloseButton && (
        <button
          className="absolute top-4 right-4 flex h-6 w-6 shrink-0 items-center justify-center md:top-8 md:right-6"
          onClick={onClose}
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#1D3146" />
          </svg>
        </button>
      )}

      {isLoginSessionExpiry ? (
        <>
          <div
            data-testid="info-modal-header"
            className="w-full border-b border-[#D7E7F8] bg-[#FEFEFE] px-6 py-4"
          >
            <span
              id={resolvedTitleId}
              className="block text-left font-[Poppins,var(--Font-Family-title,sans-serif)] text-[20px] font-semibold leading-normal text-[#12202C]"
            >
              {resolvedTitle}
            </span>
          </div>
          <div
            data-testid="info-modal-body"
            className="w-full border-b border-[#D7E7F8] bg-[#FEFEFE] px-6 py-4"
          >
            <p
              id={resolvedDescriptionId}
              className="m-0 text-left font-[var(--Font-Family-body,Poppins)] text-[15px] font-normal leading-normal text-[#12202C]"
            >
              {resolvedBody}
            </p>
          </div>
        </>
      ) : (
        <>
      <div
        className={cn(
          "flex w-full items-start justify-between",
          "px-4 pb-4 pt-4 md:px-8 md:pb-4 md:pt-8",
        )}
      >
        <span
          id={resolvedTitleId}
          className="flex-1 text-left font-[Poppins,var(--Font-Family-title,sans-serif)] text-xl font-semibold leading-normal text-[#12202C]"
        >
          {resolvedTitle}
        </span>
        {showCloseButton && (
          <span className="h-6 w-6" />
        )}
      </div>

      <div className="my-0 h-px w-full bg-gray-200" />
      <p
        id={resolvedDescriptionId}
        className="m-0 flex-1 self-stretch overflow-y-auto px-4 py-4 text-left font-[var(--Font-Family-body,Poppins)] text-[15px] font-normal leading-normal text-[#00273A] md:flex-none md:overflow-visible md:p-8"
      >
        {resolvedBody}
      </p>

      <div className="my-0 h-px w-full bg-gray-200" />
        </>
      )}

      <div
        data-testid="info-modal-actions"
        className={cn("flex w-full shrink-0 gap-4", actionsClassName)}
      >
        {!hideSecondaryAction && (
          <Button
            variant={resolvedSecondaryAction.variant ?? "outlined"}
            onClick={resolvedSecondaryAction.onClick}
            fullWidth
            className={cn(
              actionButtonClassName,
              "border border-[#00476A] text-[#00476A] md:max-w-none",
            )}
          >
            {resolvedSecondaryAction.label}
          </Button>
        )}
        <Button
          variant={resolvedPrimaryAction.variant ?? "filled"}
          onClick={resolvedPrimaryAction.onClick}
          fullWidth={!isLoginSessionExpiry}
          className={cn(actionButtonClassName, "bg-[#00476A] text-white md:max-w-none")}
        >
          {resolvedPrimaryAction.label}
        </Button>
      </div>
      </div>
    </dialog>
  );
};
